<script setup>
import { useRouter, userDataLoading } from "@/utils/vue_helpers";
import { computed } from "vue";

const COLOR_PALETTES = {
  "blue darken-1": { main: "#1565C0", soft: "#E3F2FD", icon: "#1976D2" },
  "orange darken-2": { main: "#E65100", soft: "#FFF3E0", icon: "#F57C00" },
  "indigo darken-1": { main: "#283593", soft: "#E8EAF6", icon: "#3949AB" },
  "green darken-1": { main: "#2E7D32", soft: "#E8F5E9", icon: "#43A047" },
  "deep-purple darken-1": { main: "#4527A0", soft: "#EDE7F6", icon: "#5E35B1" },
  "teal darken-1": { main: "#00695C", soft: "#E0F2F1", icon: "#00897B" },
  "red darken-2": { main: "#C62828", soft: "#FFEBEE", icon: "#E53935" },
  "blue-grey darken-1": { main: "#455A64", soft: "#ECEFF1", icon: "#546E7A" },
};

const props = defineProps({
  text: {
    type: String,
    default: null,
  },
  icon: {
    type: String,
    default: null,
  },
  number: {
    type: [String, Number],
    default: null,
  },
  accentColor: {
    type: String,
    default: "primary",
  },
  route: {
    type: Object,
    default: null,
  },
  loading: {
    type: Boolean,
    default: null,
  },
});

const { router } = useRouter();

const palette = computed(() => {
  return (
    COLOR_PALETTES[props.accentColor] || {
      main: "#4527A0",
      soft: "#EDE7F6",
      icon: "#5E35B1",
    }
  );
});

const cardStyle = computed(() => ({
  "--stat-main": palette.value.main,
  "--stat-soft": palette.value.soft,
  "--stat-icon": palette.value.icon,
}));

const isLoading = computed(() => {
  if (props.loading !== null) {
    return props.loading;
  }
  return userDataLoading.value;
});

const isClickable = computed(() => !!props.route);

function onClicked() {
  if (props.route) {
    router.value.push(props.route);
  }
}
</script>

<template>
  <v-card
    flat
    rounded="lg"
    class="stat-card fill-height"
    :class="{ 'stat-card--clickable': isClickable }"
    :style="cardStyle"
    @click="onClicked"
  >
    <div class="stat-card__accent" />

    <v-card-text class="stat-card__body pa-4 pa-md-5 d-flex flex-column fill-height">
      <div class="d-flex align-start justify-space-between mb-4">
        <div class="stat-card__icon-wrap flex-shrink-0">
          <v-icon :color="palette.icon" size="26">{{ icon }}</v-icon>
        </div>
        <v-chip
          v-if="isClickable"
          x-small
          class="stat-card__action-chip text-none font-weight-medium"
          :color="palette.main"
          outlined
        >
          View
          <v-icon right x-small>mdi-arrow-right</v-icon>
        </v-chip>
      </div>

      <div class="flex-grow-1">
        <v-skeleton-loader
          v-if="isLoading"
          type="text"
          width="80"
          height="44"
          class="mb-2 stat-card__skeleton"
        />
        <div v-else class="stat-card__count">
          {{ number }}
        </div>

        <div class="stat-card__label mt-1">
          {{ text }}
        </div>
      </div>

      <div v-if="$scopedSlots.additionalContent" class="stat-card__footer mt-3">
        <slot name="additionalContent" />
      </div>
    </v-card-text>
  </v-card>
</template>

<style scoped>
.stat-card {
  position: relative;
  overflow: hidden;
  background: var(--stat-soft) !important;
  border: 1px solid rgba(0, 0, 0, 0.06);
  transition: box-shadow 0.22s ease, transform 0.18s ease, border-color 0.22s ease;
}

.stat-card__accent {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, var(--stat-main), var(--stat-icon));
}

.stat-card__body {
  position: relative;
  z-index: 1;
}

.stat-card__icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.stat-card__count {
  font-size: 2.25rem;
  line-height: 1.1;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--stat-main);
  font-variant-numeric: tabular-nums;
}

.stat-card__label {
  font-size: 0.9375rem;
  line-height: 1.35;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.72);
}

.stat-card__action-chip {
  background: rgba(255, 255, 255, 0.72) !important;
}

.stat-card__footer {
  padding-top: 10px;
  border-top: 1px dashed rgba(0, 0, 0, 0.1);
}

.stat-card--clickable {
  cursor: pointer;
}

.stat-card--clickable:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.1) !important;
  border-color: rgba(0, 0, 0, 0.1);
}

.stat-card--clickable:hover .stat-card__action-chip {
  background: #fff !important;
}

.stat-card__skeleton >>> .v-skeleton-loader__bone {
  background: rgba(255, 255, 255, 0.65) !important;
}

@media (max-width: 600px) {
  .stat-card__count {
    font-size: 1.85rem;
  }

  .stat-card__icon-wrap {
    width: 46px;
    height: 46px;
  }
}
</style>
